import "./simple-pdf-tool.css";
import "./pdf-transform.css";
import { openBrowserPdf, pdfErrorMessage } from "./pdf-browser-core.js";
import { createOperationResult } from "./operation-result.js";
import { compressionSummary, formatFileSize, PDF_TRANSFORM_MODES, transformedPdfName } from "./pdf-transform-core.js";

const root = document.querySelector("[data-pdf-transform]");

if (root) {
  const mode = root.dataset.mode === PDF_TRANSFORM_MODES.FLATTEN ? PDF_TRANSFORM_MODES.FLATTEN : PDF_TRANSFORM_MODES.COMPRESS;
  const byId = id => document.getElementById(id);
  const elements = Object.fromEntries([
    "transformStatus", "transformUploadPanel", "transformDropZone", "chooseTransformPdf", "transformFileInput",
    "transformWorkspace", "transformFileName", "transformFileDetails", "replaceTransformPdf", "transformPreset",
    "processTransformPdf", "transformProgress", "transformResult"
  ].map(id => [id, byId(id)]));
  const state = { file: null, pageCount: 0, busy: false, worker: null };
  const operationResult = createOperationResult(root, elements.transformWorkspace);

  const showStatus = (message = "", kind = "error") => {
    elements.transformStatus.textContent = message;
    elements.transformStatus.dataset.kind = kind;
    elements.transformStatus.hidden = !message;
  };
  const updateControls = () => {
    elements.replaceTransformPdf.disabled = state.busy;
    if (elements.transformPreset) elements.transformPreset.disabled = state.busy;
    elements.processTransformPdf.disabled = state.busy || !state.file;
  };

  async function loadFile(file) {
    if (!file || state.busy) return;
    showStatus();
    state.busy = true;
    updateControls();
    let loaded;
    try {
      loaded = await openBrowserPdf(file);
      state.file = file;
      state.pageCount = loaded.pageCount;
      elements.transformFileName.textContent = file.name || "document.pdf";
      elements.transformFileDetails.textContent = `${formatFileSize(file.size)} · ${loaded.pageCount} ${loaded.pageCount === 1 ? "page" : "pages"}`;
      elements.transformUploadPanel.hidden = true;
      elements.transformWorkspace.hidden = false;
      elements.transformResult.hidden = true;
    } catch (error) {
      showStatus(pdfErrorMessage(error));
    } finally {
      if (loaded?.document) await loaded.document.destroy();
      state.busy = false;
      elements.transformFileInput.value = "";
      updateControls();
    }
  }

  function runWorker(file) {
    return new Promise(async (resolve, reject) => {
      const worker = new Worker("/dist/pdf-transform-worker.js", { type: "module" });
      state.worker = worker;
      const id = crypto.randomUUID();
      const timeout = window.setTimeout(() => finish(() => reject({ code: "WORKER_TIMEOUT" })), 120_000);
      const finish = callback => {
        window.clearTimeout(timeout);
        worker.terminate();
        if (state.worker === worker) state.worker = null;
        callback();
      };
      worker.addEventListener("message", event => {
        if (event.data?.id !== id) return;
        if (event.data.ok) finish(() => resolve(event.data.buffer));
        else finish(() => reject(new Error(event.data.detail || "The PDF could not be processed.")));
      });
      worker.addEventListener("error", () => finish(() => reject(new Error("The PDF processor could not start."))), { once: true });
      try {
        const buffer = await file.arrayBuffer();
        worker.postMessage({ id, mode, preset: elements.transformPreset?.value || "balanced", buffer }, [buffer]);
      } catch (error) { finish(() => reject(error)); }
    });
  }

  async function processPdf() {
    if (!state.file || state.busy) return;
    state.busy = true;
    showStatus();
    elements.transformProgress.hidden = false;
    elements.transformProgress.textContent = mode === PDF_TRANSFORM_MODES.COMPRESS ? "Optimizing PDF…" : "Flattening fields and annotations…";
    updateControls();
    try {
      const output = await runWorker(state.file);
      const result = compressionSummary(state.file.size, output.byteLength);
      elements.transformResult.textContent = mode === PDF_TRANSFORM_MODES.COMPRESS
        ? result
        : `${formatFileSize(output.byteLength)} · Interactive annotations were flattened.`;
      elements.transformResult.hidden = false;
      if (mode === PDF_TRANSFORM_MODES.COMPRESS && output.byteLength >= state.file.size) {
        operationResult.show({ title: "Already optimized", message: "This PDF is already as small as this tool can make it.", details: [result] });
      } else {
        operationResult.show({
          blob: new Blob([new Uint8Array(output)], { type: "application/pdf" }),
          fileName: transformedPdfName(state.file.name, mode),
          message: mode === PDF_TRANSFORM_MODES.COMPRESS ? "Your PDF was compressed successfully." : "Your PDF was flattened successfully.",
          details: [result]
        });
      }
    } catch (error) {
      showStatus(error?.message || "smallpdf.app could not process this PDF.");
    } finally {
      state.busy = false;
      elements.transformProgress.hidden = true;
      updateControls();
    }
  }

  elements.chooseTransformPdf.addEventListener("click", event => { event.stopPropagation(); elements.transformFileInput.click(); });
  elements.replaceTransformPdf.addEventListener("click", () => elements.transformFileInput.click());
  elements.transformFileInput.addEventListener("change", () => loadFile(elements.transformFileInput.files?.[0]));
  elements.processTransformPdf.addEventListener("click", processPdf);
  elements.transformDropZone.addEventListener("click", event => { if (!event.target.closest("button")) elements.transformFileInput.click(); });
  elements.transformDropZone.addEventListener("keydown", event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); elements.transformFileInput.click(); } });
  elements.transformDropZone.addEventListener("dragover", event => { event.preventDefault(); elements.transformDropZone.classList.add("is-dragging"); });
  elements.transformDropZone.addEventListener("dragleave", () => elements.transformDropZone.classList.remove("is-dragging"));
  elements.transformDropZone.addEventListener("drop", event => { event.preventDefault(); elements.transformDropZone.classList.remove("is-dragging"); loadFile(event.dataTransfer.files?.[0]); });
  window.addEventListener("beforeunload", () => { operationResult.dispose(); state.worker?.terminate(); });
  updateControls();
}
